import {
  AutoComplete,
  AutoCompleteAutomations,
  DateInput,
  FormInput,
  ISelectType,
  SwitchInput,
  useFormContext,
} from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import { memo, useCallback, useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { GlobalAxios } from "../../../../../../axios/axios-config";
import FormInformation from "../../../../../../components/FormInformation/FormInformation";
import MainApi from "../../../../../../services/mainApi.service";
import { useAutomationRules } from "../../../../../../store/contexts/AutomationRulesContext";
import { TAutomationRuleForm } from "../automationRuleSchema";
import { BoardList } from "./BoardList/BoardList";
import { IAutomationRuleBasicData } from "./models";
import { SelectTicketEvent } from "./SelectTicketEvent";
import { WhatsappTemplate } from "./WhatsappTemplate";
import TicketChannelAutomationRule from "../TicketChannelAutomationRule/TicketChannelAutomationRule";
import { useAutomation } from "../../useAutomation";
import { REAGENDAMENTO_AUTOMATION_CODES } from "../../../../../../utils/ticketAutomation";

const TICKET_SERVICES_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketServices/";
const ticketServicesService = new MainApi<ISelectType>(
  TICKET_SERVICES_ROUTE + "ByCompanyGroup/",
);

const GENERIC_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";
const genericService = new MainApi<ISelectType>(GENERIC_ROUTE);

const translationPath = "chatBot.page.automations.automationRules.";

export const BasicData = memo(
  ({ onGetTicketAutomation }: IAutomationRuleBasicData) => {
    const { t } = useTranslation();

    const { id } = useParams();

    const { control, setValue } = useFormContext<TAutomationRuleForm>();

    const { selectAutomation, selectRuleTemplate } = useAutomationRules();

    const { automationParams } = useAutomation();

    const currentAutomation = useWatch({ name: "ticketAutomation", control });

    const currentTemplate = useWatch({
      name: "ticketAutomationTemplate",
      control,
    });

    const actionAfter24HoursParams = useMemo(
      () => ({ Identifier: "TicketAutomationActionAfter24Hours" }),
      [],
    );

    const currentAutomationMemoized = useMemo(
      () => currentAutomation,
      [currentAutomation],
    );
    useEffect(() => {
      onGetTicketAutomation(
        currentAutomationMemoized
          ? {
              ...currentAutomationMemoized,
              code:
                currentAutomationMemoized && currentAutomationMemoized.code
                  ? currentAutomationMemoized.code
                  : "",
              actionTypes: (currentAutomationMemoized.actionTypes ?? []).map(
                (a) => ({
                  id: a.id,
                  name: a.name,
                  code: a.code ?? undefined,
                  description: (a as any).description ?? null,
                }),
              ),
            }
          : null,
      );
    }, [selectAutomation, onGetTicketAutomation, currentAutomationMemoized]);

    useEffect(() => {
      selectAutomation(
        currentAutomationMemoized
          ? {
              ...currentAutomationMemoized,
              code:
                currentAutomationMemoized && currentAutomationMemoized.code
                  ? currentAutomationMemoized.code
                  : undefined,
              actionTypes: (currentAutomationMemoized.actionTypes ?? []).map(
                (a) => ({
                  id: a.id,
                  name: a.name,
                  code: a.code ?? undefined,
                  description: (a as any).description ?? null,
                }),
              ),
            }
          : null,
      );
    }, [currentAutomationMemoized, selectAutomation]);

    // Template da regra (nome e ação, ex.: "1" = Nenhuma) usado nos campos da regra de mensagem
    useEffect(() => {
      selectRuleTemplate(
        currentTemplate
          ? {
              name: currentTemplate.name,
              actionTypeCode: currentTemplate.actionType?.code ?? null,
            }
          : null,
      );
    }, [currentTemplate, selectRuleTemplate]);

    const cleanTicketEvent = useCallback(() => {
      setValue("ticketEvent", null);
    }, [setValue]);

    const changeTicketServiceHandler = useCallback(() => {
      setValue("ticketAutomationTemplate", null);
    }, [setValue]);

    const showPendent = [
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "9",
      "10",
      "11",
      "12",
      "13",
      "14",
      "16",
      "17",
      "18",
      "22",
      "23",
      "24",
      "27",
      "28",
      "29",
      "30",
      "34",
      "35",
      ...REAGENDAMENTO_AUTOMATION_CODES,
    ];

    return (
      <>
        <Grid item xs={12} sm={12} textAlign={"end"}>
          <SwitchInput name="enable" label={t(translationPath + "enable")} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormInput
            name="name"
            label={t(translationPath + "name")}
            size="small"
            fullWidth
            autoFocus
          />
        </Grid>

        {id && (
          <Grid item xs={12} sm={6}>
            <FormInformation
              label={t(translationPath + "automation")}
              data={currentAutomation ? currentAutomation.name : "-"}
              minHeight={40}
            />
          </Grid>
        )}

        {!id && (
          <Grid item xs={12} sm={6}>
            <AutoCompleteAutomations
              name="ticketAutomation"
              params={automationParams}
              axios={GlobalAxios}
              label={t(translationPath + "automation")}
              getOptionLabel={(option) => option.name}
              size="small"
              onValueChange={cleanTicketEvent}
              fullWidth
            />
          </Grid>
        )}

        <SelectTicketEvent />

        <Grid item xs={12} sm={6}>
          <AutoComplete
            name="ticketService"
            label={t(translationPath + "ticket_service")}
            getOptionLabel={(option) => option.name}
            size="small"
            fullWidth
            request={ticketServicesService.list}
            onValueChange={changeTicketServiceHandler}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <AutoComplete
            name="actionAfter24Hours"
            label={t(translationPath + "action_after_24_hours")}
            getOptionLabel={(option) => option.name}
            size="small"
            fullWidth
            request={genericService.list}
            params={actionAfter24HoursParams}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <DateInput
            name="initialDate"
            label={t(translationPath + "initial_date")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DateInput name="endDate" label={t(translationPath + "end_date")} />
        </Grid>

        {currentAutomation && currentAutomation.allowWhatsappTemplate && (
          <WhatsappTemplate />
        )}

        {currentAutomation &&
          currentAutomation.code !== "8" &&
          currentAutomation.code !== "7" && (
            <BoardList
              label={t(translationPath + "robot") ?? undefined}
              boardFieldName="ticketChannelRobot"
              listFieldName="ticketChannelStepRobot"
            />
          )}

        {currentAutomation &&
          currentAutomation.allowTransfer &&
          currentAutomation.code !== "8" &&
          currentAutomation.code !== "1" &&
          currentAutomation.code !== "5" &&
          currentAutomation.code !== "11" &&
          currentAutomation.code !== "22" &&
          currentAutomation.code !== "23" &&
          currentAutomation.code !== "24" &&
          currentAutomation.code !== "34" && (
            <BoardList
              label={t(translationPath + "attendant") ?? undefined}
              boardFieldName="ticketChannelAttendant"
              listFieldName="ticketChannelStepAttendant"
            />
          )}

        {currentAutomation && showPendent.includes(currentAutomation.code!) && (
          <BoardList
            label={t(translationPath + "pendent") ?? undefined}
            boardFieldName="ticketChannelPendent"
            listFieldName="ticketChannelStepPendent"
          />
        )}

        {currentAutomation && currentAutomation.code === "8" && (
          <TicketChannelAutomationRule />
        )}
        {currentAutomation && currentAutomation.code !== "7" && (
          <>
            <BoardList
              label={t(translationPath + "when_error") ?? undefined}
              boardFieldName="ticketChannelWhenError"
              listFieldName="ticketChannelStepWhenError"
            />
            <BoardList
              label={t(translationPath + "when_blacklist") ?? undefined}
              boardFieldName="ticketChannelWhenBlacklist"
              listFieldName="ticketChannelStepWhenBlacklist"
            />
          </>
        )}
      </>
    );
  },
);
